import { ChangeEvent, useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Mixpanel } from '../../../../../../../utils/mixpanel';
import useDebounce from '../../../../../../../utils/constants/useDebounce';
import { useAppSelector } from '../../../../../../../app/hooks';
import { useDeferredUnmount } from '../../../../../../../common/hooks/useDeferredUnmount';
import { prefersReducedMotion } from '../../../../../../../utils/prefersReducedMotion';

/** How long the box takes to grow and shrink — mirrors `$box-motion-duration` in the stylesheet. */
export const BOX_TRANSITION_MS = 300;

const BOX_KEY = 'box';

export interface DropdownOption {
    id: string;
    label: string;
}

type UseSearchBoxLogicArgs = {
    setSearchTerm?: (v: string) => void;
    onSearch?: (companyName: string) => void;
    categoryParam: string;
    accessTierParam: string;
    onOptionSelect?: (id: string) => void;
    /** The three the component resolves a default for, so they arrive settled. */
    showDropdown: boolean;
    dropdownOptions: DropdownOption[];
    allowResize: boolean;
};

export const useSearchBoxLogic = ({
    setSearchTerm,
    onSearch,
    categoryParam,
    accessTierParam,
    onOptionSelect,
    showDropdown,
    dropdownOptions,
    allowResize,
}: UseSearchBoxLogicArgs) => {
    const [queryText, setQueryText] = useState('');
    const [isDropdownVisible, setIsDropdownVisible] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const { heldKeys, hold, release } = useDeferredUnmount<typeof BOX_KEY>(BOX_TRANSITION_MS);
    const debouncedQueryText = useDebounce(queryText.trim(), 300);
    const searchBoxRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    /** What the caller is already filtering by — mirrors the last query handed to `handleSearch`. */
    const searchedQuery = useRef('');
    const [queryParams] = useSearchParams();

    const selectedCategory = queryParams.get('category');

    /**
     * True while the box is open AND while it is shrinking back. Both have to be covered: the
     * expanded box's width is a percentage of this element, and the row hides its title and the
     * sibling actions to free the space. Hand either back while the box is still wide and the
     * animation's own sizing context collapses under it, so the close snaps instead of running.
     */
    const ownsRow = isExpanded || heldKeys.includes(BOX_KEY);

    const commentThreadState = useAppSelector((state) => state.commentsThreadReducer);
    const isCommentsResizeActive = commentThreadState.showCommentsThread && !commentThreadState.closingCommentsThread && allowResize;

    /** A no-op unless the box is open, so a stray outside click cannot flicker the row. */
    const startClosing = () => {
        if (!isExpanded) return;
        if (!prefersReducedMotion()) hold(BOX_KEY);
        setIsExpanded(false);
    };

    // Focus follows the growth so the caret is not placed in a box that is still resizing —
    // reduced motion has no growth to wait out. Keyed on the open state so a collapse, a reopen
    // and an unmount each cancel the wait they interrupt; a wait outliving its own transition
    // would focus the input midway through the next one.
    useEffect(() => {
        if (!isExpanded) return;
        const focusTimer = setTimeout(() => inputRef.current?.focus(), prefersReducedMotion() ? 0 : BOX_TRANSITION_MS);
        return () => clearTimeout(focusTimer);
    }, [isExpanded]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (searchBoxRef.current && !searchBoxRef.current.contains(event.target as Node)) {
                setIsDropdownVisible(false);
                if (queryText.length === 0) {
                    startClosing();
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
        // isExpanded is read through startClosing, so the listener has to be rebound when it changes
    }, [queryText, isExpanded]);

    useEffect(() => {
        // A dropdown pick searches on the click and then lands its label in the input, so the
        // debounce arrives at a query the caller is already filtering by. Repeating it would
        // double both the fetch and the Mixpanel event for every selection.
        if (debouncedQueryText === searchedQuery.current) return;
        if (debouncedQueryText.length >= 3) {
            handleSearch(debouncedQueryText);
            setHasSearched(true);
        } else if (debouncedQueryText.length === 0 && hasSearched) {
            handleSearch('');
            setHasSearched(false);
        }
    }, [debouncedQueryText]);

    // Switching what is being listed abandons the query, so the caller has to be un-filtered too —
    // emptying the input alone leaves it filtering by a term the box no longer shows, and the
    // debounced clear above cannot cover it: the `hasSearched` reset is what suppresses it.
    // One effect for all three, because Manage Lenders passes the `category` param straight back
    // in as `categoryParam` — a reset per dependency would un-filter twice on the same switch.
    useEffect(() => {
        setQueryText('');
        if (hasSearched) handleSearch('');
        setHasSearched(false);
    }, [categoryParam, accessTierParam, selectedCategory]);

    // Kept out of the reset: only a `selectedCategory` change navigates the row out from under an
    // open box, and folding it in would collapse the box on a plain access-tier switch too.
    useEffect(() => {
        startClosing();
    }, [selectedCategory]);

    const handleSearch = (query: string) => {
        searchedQuery.current = query;
        setSearchTerm?.(query);
        onSearch?.(query);
        if (query.length >= 3) {
            const trackProperties = {
                search_query: query,
                search_length: query.length,
                view: categoryParam,
                timestamp: new Date().toISOString(),
            };
            Mixpanel.track('Search Button Click', trackProperties);
        }
    };

    const handleTextSearch = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setQueryText(value);
        setIsDropdownVisible(value.length >= 3 && showDropdown);
    };

    const handleOptionSelection = (id: string) => {
        setIsDropdownVisible(false);
        const selected = dropdownOptions.find((option) => option.id === id);
        if (selected) {
            setQueryText(selected.label);
            onOptionSelect?.(id);
            handleSearch(selected.label);
            setHasSearched(true);
        }
    };

    const handleExpandSearch = () => {
        release(BOX_KEY);
        setIsExpanded(true);
    };

    const handleCollapseSearch = () => {
        startClosing();
        // Only trigger search reset if a search was previously executed
        if (hasSearched) {
            setQueryText('');
            handleSearch('');
            setHasSearched(false);
        } else {
            setQueryText('');
        }
    };

    return {
        queryText,
        isDropdownVisible,
        isExpanded,
        ownsRow,
        isCommentsResizeActive,
        searchBoxRef,
        inputRef,
        handleTextSearch,
        handleOptionSelection,
        handleExpandSearch,
        handleCollapseSearch,
    };
};
